import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

export default function VerifyMember() {
  const { membershipId } = useParams()
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [manualId, setManualId] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const verify = async () => {
      setLoading(true)
      const { data: supporter } = await supabase
        .from('supporters')
        .select('*, chapters(name), clubs(name)')
        .eq('membership_id', membershipId)
        .single()

      if (!supporter) { setResult({ valid: false }); setLoading(false); return }

      const { data: sub } = await supabase
        .from('subscriptions').select('valid_until')
        .eq('supporter_id', supporter.id)
        .order('valid_until', { ascending: false })
        .limit(1).single()

      const isPaid = sub && new Date(sub.valid_until) >= new Date()

      setResult({
        valid: true,
        isPaid,
        supporter,
        validUntil: sub ? new Date(sub.valid_until).toLocaleDateString('en-US', {
          month: 'long', day: 'numeric', year: 'numeric'
        }) : null,
      })
      setLoading(false)
    }
    verify()
  }, [membershipId])

  const handleManualLookup = (e) => {
    e.preventDefault()
    if (!manualId.trim()) return
    setManualId('')
    navigate(`/verify/${manualId.trim()}`)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-green-400 text-sm uppercase tracking-widest animate-pulse">Verifying...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-4 py-16">

      {/* Logo */}
      <Link to="/" className="flex items-center gap-3 mb-12">
        <img src="/Mufulira_Wanderers_logo.png" alt="Mufulira Wanderers" className="w-10 h-10 object-contain" />
        <span className="font-black text-white tracking-widest uppercase">DSMS</span>
      </Link>

      {/* Result */}
      {!result.valid ? (
        <div className="text-center w-full max-w-sm">
          <div className="w-20 h-20 rounded-full bg-red-900/30 border-2 border-red-600 flex items-center justify-center mx-auto mb-6">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-500">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <div className="text-red-500 font-black text-2xl uppercase tracking-tight mb-2">Not Verified</div>
          <p className="text-gray-500 text-sm">No member found with ID: <span className="text-white font-mono">{membershipId}</span></p>
        </div>

      ) : !result.isPaid ? (
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full bg-yellow-900/30 border-2 border-yellow-600 flex items-center justify-center mx-auto mb-4">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-500">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div className="text-yellow-500 font-black text-2xl uppercase tracking-tight mb-1">Subscription Inactive</div>
            <p className="text-gray-500 text-sm">Member exists but subscription is not active</p>
          </div>
          <MemberCard supporter={result.supporter} />
        </div>

      ) : (
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center mx-auto mb-4">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-400">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div className="text-green-400 font-black text-2xl uppercase tracking-tight mb-1">Verified ✓</div>
            <p className="text-gray-500 text-sm">Active paid member · Valid until <span className="text-green-400 font-bold">{result.validUntil}</span></p>
          </div>
          <MemberCard supporter={result.supporter} />
        </div>
      )}

      {/* Actions */}
      <div className="w-full max-w-sm mt-8 flex flex-col gap-3">
        <button
          onClick={() => navigate('/admin/scan')}
          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="4" height="4"/>
          </svg>
          Scan Another Card
        </button>

        <form onSubmit={handleManualLookup} className="flex gap-2">
          <input
            type="text"
            value={manualId}
            onChange={(e) => setManualId(e.target.value)}
            placeholder="Enter membership ID..."
            className="flex-1 bg-white/5 border border-white/10 focus:border-green-600 text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-600"
          />
          <button
            type="submit"
            className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap"
          >
            Look Up
          </button>
        </form>
      </div>
    </div>
  )
}

function MemberCard({ supporter }) {
  const initials = supporter.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()
  return (
    <div className="bg-white/5 border border-white/10 p-6">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-14 rounded-full overflow-hidden bg-green-900/50 border-2 border-green-700/50 flex items-center justify-center text-white font-black text-lg shrink-0">
          {supporter.profile_photo ? (
            <img src={supporter.profile_photo} alt="Profile" className="w-full h-full object-cover" />
          ) : initials}
        </div>
        <div>
          <div className="font-black text-white text-lg uppercase tracking-tight">{supporter.full_name}</div>
          <div className="text-green-400 text-xs font-bold tracking-widest mt-0.5">{supporter.membership_id}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
        {[
          { label: 'Club', value: supporter.clubs?.name },
          { label: 'Chapter', value: supporter.chapters?.name || 'N/A' },
          { label: 'Gender', value: supporter.gender || 'N/A' },
          { label: 'Region', value: supporter.region || 'N/A' },
        ].map(item => (
          <div key={item.label}>
            <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{item.label}</div>
            <div className="text-white text-sm font-semibold capitalize">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}